import { useState, useEffect } from "react";
import Swal from "sweetalert2";

import {
  LayoutDashboard,
  Pill,
  Tablets,
  Hospital,
  Store,
  FlaskRound,
  Users,
  Truck,
  ClipboardList,
  Archive,
  CircleDollarSign,
  Activity,
  FileBarChart,
  FolderOpen,
  Package,
  Boxes,
  ReceiptText,
  ClipboardCheck,
  TestTube,
  Scale,
  ScrollText,
  ChartNoAxesCombined,
  ChartBarStacked,
} from "lucide-react";

import { FaChevronRight } from "react-icons/fa";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const Sidebar = ({ isCollapsed }) => {
  const [openMenu, setOpenMenu] = useState(null);
  const [role, setRole] = useState(localStorage.getItem("role"));
  const [ripples, setRipples] = useState({});
  const [subRipples, setSubRipples] = useState({});

  useEffect(() => {
    const storedRole = localStorage.getItem("role");
    setRole(storedRole);
  }, []);

  const createRipple = (event, idx, type = "main") => {
    const rect = event.currentTarget.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    const newRipple = { x, y, size, id: Date.now() };

    if (type === "main") {
      setRipples((prev) => ({
        ...prev,
        [idx]: [...(prev[idx] || []), newRipple],
      }));
      setTimeout(() => {
        setRipples((prev) => ({
          ...prev,
          [idx]: (prev[idx] || []).filter((r) => r.id !== newRipple.id),
        }));
      }, 500);
    } else {
      setSubRipples((prev) => ({
        ...prev,
        [idx]: [...(prev[idx] || []), newRipple],
      }));
      setTimeout(() => {
        setSubRipples((prev) => ({
          ...prev,
          [idx]: (prev[idx] || []).filter((r) => r.id !== newRipple.id),
        }));
      }, 500);
    }
  };

  const toggleMenu = (label) => {
    setOpenMenu(openMenu === label ? null : label);
  };

  const menuItems = [
    {
      to: "/",
      end: true,
      label: "Dashboard",
      icon: <LayoutDashboard className="w-5 h-5" />,
      release: true,
    },
    {
      label: "Enterprise",
      icon: <Hospital className="w-5 h-5" />,
      children: [
        {
          to: "/enterprise/outlet",
          label: "Outlets",
          icon: <Store className="w-5 h-5" />,
          roles: ["owner"],
          release: true,
        },
        {
          to: "/enterprise/shift",
          label: "Shift",
          icon: <ClipboardList className="w-5 h-5" />,
          roles: ["owner"],
          release: true,
        },
        {
          to: "/enterprise/karyawan",
          label: "Karyawan",
          icon: <Users className="w-5 h-5" />,
          roles: ["owner", "manager"],
          release: true,
        },
        {
          to: "/enterprise/distributor",
          label: "Distributor",
          icon: <Truck className="w-5 h-5" />,
          roles: ["owner"],
          release: true,
        },
      ],
    },

    {
      label: "Inventory",
      icon: <Archive className="w-5 h-5" />,
      children: [
        {
          to: "/inventory/stock-opname",
          label: "Stock Opname",
          icon: <Boxes className="w-5 h-5" />,
          roles: ["owner", "manager"],
          release: true,
        },
        {
          to: "/inventory/purchase-order",
          label: "Purchase Order",
          icon: <ClipboardCheck className="w-5 h-5" />,
          roles: ["owner", "manager"],
          release: true,
        },
        {
          to: "/inventory/invoice",
          label: "Invoice",
          icon: <ReceiptText className="w-5 h-5" />,
          roles: ["owner", "manager"],
          release: true,
        },
      ],
    },

    {
      label: "Obat & Paket",
      icon: <Pill className="w-5 h-5" />,
      children: [
        {
          label: "Obat",
          to: "/obat-paket/obat",
          icon: <Tablets className="w-5 h-5" />,
          roles: ["owner", "manager"],
          release: true,
        },
        {
          label: "Paket",
          to: "/obat-paket/paket",
          icon: <Package className="w-5 h-5" />,
          roles: ["owner", "manager"],
          release: true,
        },
      ],
    },

    {
      label: "Transaction",
      to: "/transactions",
      icon: <CircleDollarSign className="w-5 h-5" />,
      end: true,
      release: true,
    },

    {
      label: "Detailed Report",
      icon: <ScrollText className="w-5 h-5" />,
      children: [
        {
          label: "Sales Report",
          icon: <ChartNoAxesCombined className="w-5 h-5" />,
          to: "/report/salesreport",
          roles: ["owner", "manager"],
          release: true,
          end: true,
        },
        {
          label: "Monthly Report",
          icon: <ChartBarStacked className="w-5 h-5" />,
          to: "/report/monthlyreport",
          roles: ["owner", "manager"],
          release: true,
          end: true,
        },
      ],
    },

    {
      label: "Rebalance",
      to: "/rebalance",
      icon: <Scale className="w-5 h-5" />,
      roles: ["owner", "manager"],
      release: true,
    },

    {
      label: "Logs",
      to: "/logs",
      icon: <Activity className="w-5 h-5" />,
      roles: ["owner", "manager"],
      release: true,
    },
  ];

  const subMenuVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.1, duration: 0.3 },
    }),
    exit: { opacity: 0, x: -10, transition: { duration: 0.2 } },
  };

  const handleClickMenuOrSubMenu = (event, idx, type = "main", released = true) => {
    createRipple(event, idx, type);

    if (!released) {
      event.preventDefault();
      Swal.fire({
        title: "Coming Soon",
        text: "This feature is coming soon!",
        icon: "info",
      });
    }
  };

  return (
    <aside className="w-64 fixed bg-white shadow-lg p-4 transition-all duration-300 border-r border-gray-200 h-full overflow-y-auto">
      <div className="pl-2 text-left mb-6 text-[#292930]">
        <h2 className="text-4xl font-semibold">Djaya</h2>
        <h2 className="text-4xl font-black">Medica</h2>
      </div>

      <nav className="flex flex-col gap-1 px-2">
        {menuItems.map((menu, idx) =>
          menu.children ? (
            <div key={idx} className="gap-1 overflow-hidden transition-all duration-300">
              <div
                className="mb-1 flex items-center justify-between p-3 rounded-xl cursor-pointer hover:bg-green-100 transition-all"
                onClick={() => toggleMenu(menu.label)}
              >
                <div className="flex gap-2 items-center text-[#292930]">
                  {menu.icon}
                  {menu.label}
                </div>
                <FaChevronRight
                  className={`text-gray-500 transition-transform duration-300 ${
                    openMenu === menu.label ? "rotate-90" : ""
                  }`}
                />
              </div>

              <AnimatePresence>
                {openMenu === menu.label && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="ml-7 overflow-hidden"
                  >
                    <div className="flex flex-col gap-1 py-2">
                      {menu.children
                        .filter((sub) => sub.roles.includes(role))
                        .map((subMenu, subIdx) => (
                          <motion.div
                            key={subIdx}
                            custom={subIdx}
                            variants={subMenuVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                          >
                            <NavLink
                              to={subMenu.to}
                              end={subMenu.end}
                              className={`relative p-3 rounded-xl flex items-center gap-2 overflow-hidden text-[#292930] hover:bg-green-100`}
                              onClick={(event) =>
                                handleClickMenuOrSubMenu(event, `${idx}-${subIdx}`, "sub", subMenu.release)
                              }
                            >
                              {(subRipples[`${idx}-${subIdx}`] || []).map((ripple) => (
                                <motion.span
                                  key={ripple.id}
                                  initial={{ scale: 0, opacity: 0.7 }}
                                  animate={{ scale: 1.5, opacity: 0 }}
                                  transition={{ duration: 0.5 }}
                                  style={{
                                    left: ripple.x,
                                    top: ripple.y,
                                    width: ripple.size,
                                    height: ripple.size,
                                  }}
                                  className="absolute bg-green-300 rounded-full pointer-events-none"
                                />
                              ))}
                              {subMenu.icon}
                              {subMenu.label}
                            </NavLink>
                          </motion.div>
                        ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <NavLink
              to={menu.to}
              key={menu.to}
              end={menu.end}
              className={`relative p-3 rounded-xl flex items-center gap-2 overflow-hidden text-[#292930] hover:bg-green-100`}
              onClick={(event) => handleClickMenuOrSubMenu(event, idx, "main", menu.release)}
            >
              {(ripples[idx] || []).map((ripple) => (
                <motion.span
                  key={ripple.id}
                  initial={{ scale: 0, opacity: 0.7 }}
                  animate={{ scale: 1.5, opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  style={{
                    left: ripple.x,
                    top: ripple.y,
                    width: ripple.size,
                    height: ripple.size,
                  }}
                  className="absolute bg-green-300/50 rounded-full pointer-events-none"
                />
              ))}
              {menu.icon}
              {menu.label}
            </NavLink>
          )
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
